import { Component, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { firstValueFrom } from 'rxjs'
import { KirjanpitajanNimitiedot } from '../../_jaettu-lemonator/model/kirjanpitaja'
import { LlmGlobaaliPromptRow } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'
import { callLlmTestaus } from './llm-kirjausehdotus-testaus.client'
import { luoPromptiversioVertailu, PromptiversioVertailu, VertailtavaPromptiversio } from './prompti-versiovertailu.component'

@Component({
  templateUrl: './globaali-prompt.dialog.html',
  styleUrls: ['./llm-kirjausehdotus-testaus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GlobaaliPromptDialog implements OnInit {
  readonly globaaliPromptUusin = signal<LlmGlobaaliPromptRow | null>(null)
  globaaliPromptKentta = form(signal<string | null>(''))
  readonly globaaliPromptLataa = signal(false)
  readonly globaaliPromptTallennetaan = signal(false)
  readonly globaaliPromptHistoria = signal<LlmGlobaaliPromptRow[]>([])
  readonly globaaliPromptHistoriaLataa = signal(false)
  readonly naytaGlobaaliHistoria = signal(false)
  esikatseluGlobaaliVersio: LlmGlobaaliPromptRow | null = null
  readonly esikatseluVertailu = signal<PromptiversioVertailu | null>(null)

  private readonly _kirjanpitajatMap = signal<Map<string, KirjanpitajanNimitiedot>>(new Map())

  constructor(
    private _dialogRef: MatDialogRef<GlobaaliPromptDialog>,
    private _firebaseLemonator: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _dialog: MatDialog,
    private _kirjanpitajaService: KirjanpitajaService,
  ) {}

  ngOnInit() {
    this._lataaGlobaali()
    firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable).then(map => {
      this._kirjanpitajatMap.set(map)
    })
  }

  annaPaivittajanNimi(uid: string | null | undefined): string {
    if (!uid) {
      return '—'
    }
    const k = this._kirjanpitajatMap().get(uid)
    return k ? `${k.etunimi} ${k.sukunimi}` : uid
  }

  trackByVersio(_index: number, t: { versio: number }): number {
    return t.versio
  }

  sulje() {
    this._dialogRef.close()
  }

  private async _lataaGlobaali() {
    this.globaaliPromptLataa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeGlobaaliPromptUusin', {})
      if (vastaus.e || !vastaus.rivi) {
        return
      }
      this.globaaliPromptUusin.set(vastaus.rivi)
      asetaArvo(this.globaaliPromptKentta, vastaus.rivi.prompt ?? null)
      this._paivitaVertailu()
    } catch (err) {
      console.error('_lataaGlobaali virhe', err)
    } finally {
      this.globaaliPromptLataa.set(false)
    }
  }

  async tallennaGlobaali() {
    if (this.globaaliPromptKentta().value() == null) {
      this._snackbar.open('Täytä promptikenttä', 'Sulje', { duration: 4000 })
      return
    }
    this.globaaliPromptTallennetaan.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'tallennaGlobaaliPrompt', { prompt: this.globaaliPromptKentta().value() })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open(`Globaali prompt tallennettu (v${vastaus.versio})`, 'Sulje', { duration: 3000 })
      await this._lataaGlobaali()
      if (this.naytaGlobaaliHistoria()) {
        await this._lataaGlobaaliHistoria()
      }
    } catch (err) {
      console.error('tallennaGlobaali virhe', err)
      this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.globaaliPromptTallennetaan.set(false)
    }
  }

  async toggleGlobaaliHistoria() {
    this.naytaGlobaaliHistoria.set(!this.naytaGlobaaliHistoria())
    if (this.naytaGlobaaliHistoria() && !this.globaaliPromptHistoria().length) {
      await this._lataaGlobaaliHistoria()
    }
  }

  private async _lataaGlobaaliHistoria() {
    this.globaaliPromptHistoriaLataa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeGlobaaliPromptHistoria', {})
      this.globaaliPromptHistoria.set(vastaus.rivit ?? [])
      this._paivitaVertailu()
    } catch (err) {
      console.error('_lataaGlobaaliHistoria virhe', err)
    } finally {
      this.globaaliPromptHistoriaLataa.set(false)
    }
  }

  esikatseleGlobaali(versio: LlmGlobaaliPromptRow) {
    this.esikatseluGlobaaliVersio = versio
    this._paivitaVertailu()
  }

  suljeEsikatseluGlobaali() {
    this.esikatseluGlobaaliVersio = null
    this._paivitaVertailu()
  }

  private _paivitaVertailu() {
    const muunna = (rivi: LlmGlobaaliPromptRow): VertailtavaPromptiversio => ({ versio: rivi.versio, paivitetty: rivi.paivitetty, teksti: rivi.prompt })
    this.esikatseluVertailu.set(this.esikatseluGlobaaliVersio ? luoPromptiversioVertailu(this.esikatseluGlobaaliVersio, this.globaaliPromptHistoria(), this.globaaliPromptUusin(), muunna) : null)
  }

  async palautaGlobaaliVersio(versio: number) {
    const data: AreYouSureDialogData = {
      text: `Palauta globaali prompt versio ${versio}? Toiminto luo uuden version, jonka teksti on valitun version teksti.`,
      header: 'Palauta versio',
      rightAction: 'Palauta',
    }
    if (!(await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed()))) {
      return
    }
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'palautaGlobaaliPromptVersio', { versio })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Palautus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open(`Versio ${versio} palautettu uutena versiona v${vastaus.versio}`, 'Sulje', { duration: 3000 })
      this.suljeEsikatseluGlobaali()
      await this._lataaGlobaali()
      await this._lataaGlobaaliHistoria()
    } catch (err) {
      console.error('palautaGlobaaliVersio virhe', err)
      this._snackbar.open('Palautus epäonnistui', 'Sulje', { duration: 4000 })
    }
  }
}
